import api from './api';

export type MountType = 'ceiling' | 'wall';

export interface CameraSpec {
  x: number;
  y: number;
  heading_deg: number;
  tilt_deg: number;
  height_m: number;
  fov_h_deg: number;
  fov_v_deg: number;
  max_range_m: number;
  mount_type: MountType;
  cctv_id?: number | null;
  name?: string | null;
  resolution_w?: number;
  resolution_h?: number;
  focal_length_mm?: number;
  sensor_width_mm?: number;
  /** 벽: 풀 가장자리 → 카메라 (m) */
  pool_edge_offset_m?: number;
  /** 벽: 벽면 → 카메라 (m) */
  wall_mount_offset_m?: number;
  /** @deprecated */
  fov_deg?: number;
  max_range?: number;
}

export interface LayoutSimResult {
  coverage_percent: number;
  blind_percent: number;
  overlap_percent: number;
  pool_cells: number;
  camera_count: number;
  heatmap: number[][];
  pool_area_m2?: number;
  covered_area_m2?: number;
  pool_length_m?: number;
  pool_width_m?: number;
  cameras_by_mount?: { ceiling?: number; wall?: number };
  footprints?: { name?: string; mount_type?: string; polygon: number[][] }[];
  model?: string;
}

export interface CameraLayoutPayload {
  pool_id?: number;
  pool_polygon: number[][];
  cameras: CameraSpec[];
  pool_length_m?: number;
  pool_width_m?: number;
  lane_count?: number;
  ceiling_grid_cols?: number;
  ceiling_grid_rows?: number;
  wall_grid_cols?: number;
  wall_grid_rows?: number;
  wall_left_count?: number;
  wall_right_count?: number;
  wall_pool_edge_offset_m?: number;
  wall_pool_edge_offset_top_m?: number;
  wall_pool_edge_offset_bottom_m?: number;
  wall_pool_edge_offset_left_m?: number;
  wall_pool_edge_offset_right_m?: number;
  wall_mount_offset_m?: number;
  default_focal_length_mm?: number;
  default_fov_h_deg?: number;
  default_fov_v_deg?: number;
  lens_fixed?: boolean;
  /** ceiling | wall — 시방 입력 복원용 */
  mount_mode?: 'ceiling' | 'wall' | string;
  /** empty: 저장 없음 / saved: 파일 저장 / cctv_location_default: 구버전 기본 */
  source?: string;
}

/** 단거리 25m × 12m, 4레인 기본 */
export const DEFAULT_POOL_LENGTH_M = 25;
export const DEFAULT_POOL_WIDTH_M = 12;
export const DEFAULT_LANE_COUNT = 4;

export const MOUNT_DEFAULTS: Record<MountType, Partial<CameraSpec>> = {
  ceiling: {
    mount_type: 'ceiling',
    height_m: 4.5,
    tilt_deg: 90,
    fov_h_deg: 90,
    fov_v_deg: 65,
    max_range_m: 25,
  },
  wall: {
    mount_type: 'wall',
    height_m: 3.0,
    tilt_deg: 20,
    fov_h_deg: 80,
    fov_v_deg: 50,
    max_range_m: 35,
  },
};

export async function simulateCameraLayout(
  poolId: number,
  pool_polygon: number[][],
  cameras: CameraSpec[],
  opts: { grid_size?: number; pool_length_m: number; pool_width_m: number },
): Promise<LayoutSimResult> {
  return api.post<LayoutSimResult>(
    `/api/pools/${poolId}/camera-layout/simulate`,
    {
      pool_polygon,
      cameras,
      grid_size: opts.grid_size ?? 50,
      pool_length_m: opts.pool_length_m,
      pool_width_m: opts.pool_width_m,
    },
    { silentError: true },
  );
}

export async function fetchCameraLayout(poolId: number): Promise<CameraLayoutPayload> {
  return api.get<CameraLayoutPayload>(`/api/pools/${poolId}/camera-layout`, undefined, {
    useCache: false,
    silentError: true,
  });
}

export async function saveCameraLayout(
  poolId: number,
  payload: {
    pool_polygon: number[][];
    cameras: CameraSpec[];
    pool_length_m: number;
    pool_width_m: number;
    lane_count: number;
    ceiling_grid_cols?: number;
    ceiling_grid_rows?: number;
    wall_grid_cols?: number;
    wall_grid_rows?: number;
    wall_left_count?: number;
    wall_right_count?: number;
    wall_pool_edge_offset_m?: number;
    wall_pool_edge_offset_top_m?: number;
    wall_pool_edge_offset_bottom_m?: number;
    wall_pool_edge_offset_left_m?: number;
    wall_pool_edge_offset_right_m?: number;
    wall_mount_offset_m?: number;
    default_focal_length_mm?: number;
    default_fov_h_deg?: number;
    default_fov_v_deg?: number;
    lens_fixed?: boolean;
    mount_mode?: 'ceiling' | 'wall';
  },
): Promise<CameraLayoutPayload> {
  return api.put<CameraLayoutPayload>(`/api/pools/${poolId}/camera-layout`, payload, {
    silentError: true,
  } as any);
}
